import type {
  ZapierDeliveryStatus,
  ZapierSubscription
} from "@/vuex/modules/zapierSubscriptions";

export const RESUME_HINT = "Turning the Zap on in Zapier resumes deliveries.";

const STOPPED_REASON_COPY: Record<string, string> = {
  gone: "Zapier reported this Zap is off",
  unsubscribed: "Unsubscribed in Zapier",
  tenant_cancelled: "Stopped when the account was cancelled",
  // Engine Zaps only: Sera stopped sending.
  failing: "Stopped after failing for 5 days",
  integration_off: "Stopped while the Zapier integration was off",
  admin: "Turned off in Sera",
  paused: "Paused in Sera"
};

// Archived rows from before the reason was recorded carry null.
export function stoppedReasonCopy(
  reason: ZapierSubscription["archived_reason"]
): string {
  return (reason && STOPPED_REASON_COPY[reason]) || "Stopped";
}

export const DELIVERY_STATUSES: ZapierDeliveryStatus[] = [
  "success",
  "failure",
  "unsubscribed",
  "cancelled",
  "queued"
];

const STATUS_STYLES: Record<
  ZapierDeliveryStatus,
  { label: string; pill: string }
> = {
  success: { label: "Success", pill: "bg-green-50 text-green-700" },
  failure: { label: "Failed", pill: "bg-red-50 text-red-700" },
  unsubscribed: { label: "Unsubscribed", pill: "bg-yellow-50 text-yellow-600" },
  cancelled: { label: "Cancelled", pill: "bg-gray-100 text-gray-600" },
  queued: { label: "Queued", pill: "bg-blue-50 text-blue-700" }
};

export function statusStyle(status: string) {
  return (
    STATUS_STYLES[status as ZapierDeliveryStatus] ?? {
      label: status,
      pill: "bg-gray-100 text-gray-600"
    }
  );
}

export const ENGINE_BADGE = "Sera app 2.0";
export const ENGINE_BADGE_TITLE =
  "This Zap uses the newer Sera app in Zapier, which receives Sera's webhook events.";

export function isEngine(sub: Pick<ZapierSubscription, "source">): boolean {
  return sub.source === "engine";
}

export function destinationLabel(sub: ZapierSubscription): string {
  return sub.destination.zapier_account
    ? `Zapier account ${sub.destination.zapier_account}`
    : sub.destination.host;
}

export interface SubscriptionGroup {
  key: string;
  label: string;
  subscriptions: ZapierSubscription[];
}

// Zapier accounts first, in the order the API returned them, then any other
// destination hosts. Order within a group is the API's (trigger label, then
// created_at).
export function groupActiveSubscriptions(
  subscriptions: ZapierSubscription[]
): SubscriptionGroup[] {
  const zapier = new Map<string, SubscriptionGroup>();
  const other = new Map<string, SubscriptionGroup>();

  subscriptions.forEach((sub) => {
    const account = sub.destination.zapier_account;
    const groups = account ? zapier : other;
    const key = account ? `zapier:${account}` : `host:${sub.destination.host}`;
    if (!groups.has(key)) {
      groups.set(key, { key, label: destinationLabel(sub), subscriptions: [] });
    }
    groups.get(key)!.subscriptions.push(sub);
  });

  return [...zapier.values(), ...other.values()];
}
